import type { GetRefillDetailedResponseT } from '@lotushealth/lotushealth_js_sdk/schemas';

import { toTableCellText } from '@/shared/lib/table';
import { LabeledFieldGrid } from '@/shared/ui/primitives';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/shared/ui/shadcn/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/ui/shadcn/tabs';

import { buildMedicationLabel } from '../lib';
import { RefillApproveForm } from './RefillApproveForm';
import { RefillDenyForm } from './RefillDenyForm';
import { RefillPrescriptionSections } from './RefillPrescriptionSections';

export interface RefillDetailSheetProps {
  readonly refill: GetRefillDetailedResponseT | null;
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
}

export function RefillDetailSheet({ refill, open, onOpenChange }: RefillDetailSheetProps) {
  const medicationLabel = buildMedicationLabel(
    refill?.RequestedPrescription,
    refill?.OriginalPrescription ?? refill?.DispensedPrescription
  );
  const refillIdLabel = toTableCellText(refill?.RefillId);
  const requestedDateLabel = toTableCellText(refill?.RequestedDate);
  const pharmacyLabel = toTableCellText(refill?.PharmacyId);
  // ``RefillId`` is ``number | null | undefined`` on the SDK type — only mount
  // action forms when we have a usable identifier to send back.
  const actionableRefillId = typeof refill?.RefillId === 'number' ? refill.RefillId : null;
  const closeSheet = () => onOpenChange(false);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="sm:max-w-xl">
        <SheetHeader>
          <SheetTitle>Refill #{refillIdLabel}</SheetTitle>
          <SheetDescription>{medicationLabel}</SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-auto px-4 pb-4">
          <LabeledFieldGrid
            className="mb-3"
            items={[
              { label: 'Requested', value: requestedDateLabel },
              { label: 'Pharmacy', value: pharmacyLabel },
            ]}
          />

          {refill !== null ? (
            <RefillPrescriptionSections
              requested={refill.RequestedPrescription}
              original={refill.OriginalPrescription}
              dispensed={refill.DispensedPrescription}
            />
          ) : null}

          {actionableRefillId !== null ? (
            <div className="mt-4 border-t pt-4">
              <Tabs defaultValue="approve">
                <TabsList aria-label="Refill actions">
                  <TabsTrigger value="approve">Approve</TabsTrigger>
                  <TabsTrigger value="deny">Deny</TabsTrigger>
                </TabsList>
                <TabsContent value="approve" className="mt-3">
                  {/* ``key`` resets form state when the user picks a different refill. */}
                  <RefillApproveForm
                    key={`approve-${actionableRefillId}`}
                    refillId={actionableRefillId}
                    onCompleted={closeSheet}
                  />
                </TabsContent>
                <TabsContent value="deny" className="mt-3">
                  <RefillDenyForm
                    key={`deny-${actionableRefillId}`}
                    refillId={actionableRefillId}
                    onCompleted={closeSheet}
                  />
                </TabsContent>
              </Tabs>
            </div>
          ) : null}
        </div>
      </SheetContent>
    </Sheet>
  );
}
